import { useQuery } from '@tanstack/react-query';
import { useAuthUserId } from '@lucid/auth';
import { LuppApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import { GetPartnerUsersResponse, UseGetPartnerUsersParams } from './dto.onboarding';
import { partnerUsersResponseSchema } from './validators';

/*
|--------------------------------------------------------------------------
| GET PARTNER USERS
|--------------------------------------------------------------------------
*/

/**
 * Fetches partner users from the backend API with pagination support.
 * Uses authenticated user's Auth0 ID (automatically injected via LuppApi interceptor).
 *
 * @param {UseGetPartnerUsersParams} params - Hook parameters
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.perPage=10] - Number of items per page
 * @param {boolean} [params.enabled=true] - Whether the query should run
 * @returns {UseQueryResult} React Query result with user data, loading state, and error
 *
 * @example
 * const { data, isLoading, error } = useGetPartnerUsers({
 *   page: 0,
 *   perPage: 25,
 *   enabled: true
 * });
 */
export const useGetPartnerUsers = ({
  page = 0,
  perPage = 10,
  enabled = true,
}: UseGetPartnerUsersParams = {}) => {
  const userId = useAuthUserId();

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PARTNER_USERS, userId, page, perPage],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
      });

      const response = await LuppApi.get<GetPartnerUsersResponse>(
        `sales/v1/partners/users?${queryParams.toString()}`,
        {
          extractData: (response) => response, // Return full response
        }
      );

      // Validate response with Zod schema
      const validated = partnerUsersResponseSchema.parse(response);

      return {
        items: validated.data,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};
